import { useEffect } from 'react';
import { mutate } from 'swr';
import useProductRemoveFromCartTracking from 'hooks/tracking/useProductRemoveFromCartTracking';
import useProductAddToCartTracking from 'hooks/tracking/useProductAddToCartTracking';
import { useSettings } from 'contexts/SettingContext';
import { useGlobalModules } from 'contexts';
import { parseShopifyGid, getShopifyGid } from 'lib/shopify/shopifyGid';
import { getCurrencyCode } from 'lib/shopify';
import { get, set, keys } from 'lib/utils/sessionStorage';
import { useUpdateItemQuantity } from './useUpdateItemQuantity';
import { useAddItemsToCart } from './useAddItemsToCart';

// Line-item cart mutation, pricing and tracking logic shared by
// components/account/cart/lineItem (old drawer + account cart) and
// components/cart/cartLineItem (new drawer). Extracted 1:1 from lineItem.jsx.
export function useLineItemActions({ locale, lineItem, cart, contentfulData }) {
  const updateItemQuantity = useUpdateItemQuantity();
  const addItems = useAddItemsToCart();
  const { removeProduct, disableFreeItem, trackedItemsSelectList } = useSettings();
  const globalModules = useGlobalModules();
  const { freeItem } = globalModules || {};

  useEffect(() => {
    // if lineitem has no merchandise (became draft in shopify), revalidate swr cart
    if (!lineItem.merchandise) {
      mutate('/api/shopify/checkout/request/');
    }
  }, [lineItem.merchandise]);

  const relatedProducts = lineItem?.merchandise?.product?.relatedProducts?.value;
  const hasFreeItemTag = lineItem?.merchandise?.product?.tags?.includes('free-item');
  const hasFreeItemCustomAttribute = lineItem?.attributes?.find(attr => attr.key === '_freeItem');
  const lineItemIsFreeItem =
    lineItem?.merchandise?.product?.id ===
    getShopifyGid('Product', freeItem?.product?.shopifyData?.variants[0]?.product_id);

  const createTrackingPayload = (item, productSelectListObj, isCompletelyRemoved = false) => {
    const sellingPlan = item.sellingPlanAllocation?.sellingPlan;
    const keuneRepeat = (sellingPlan?.options?.[0]?.value || '').split(' ')[0];

    return {
      name: item.merchandise?.product.title,
      id: parseShopifyGid(item.merchandise?.product.id),
      category: item.merchandise?.product.productType,
      price: item.merchandise?.priceV2.amount,
      quantity: isCompletelyRemoved ? item.quantity : 1,
      productSpecsCollection: contentfulData?.productSpecsCollection?.items || [],
      itemListName: productSelectListObj?.itemListName,
      currencyCode: getCurrencyCode(locale),
      keune_repeat: keuneRepeat,
      compare_at_price: item.merchandise?.compareAtPriceV2?.amount || null,
      image_url: item.merchandise?.image?.transformedSrc || null,
    };
  };

  const getNewQuantity = (id, difference) => {
    if (!id) return 0;
    const currentQuantity = cart.lines?.edges?.reduce((acc, curr) => {
      if (curr.node.id === id) {
        return acc + curr.node.quantity;
      }
      return acc;
    }, 0);
    let newQuantity = currentQuantity - difference;
    if (newQuantity < 0) newQuantity = 0;

    return newQuantity;
  };

  const getFreeItemsQuantity = mainProductQuantityDifference => {
    if (!relatedProducts) return [];
    const parsedRelatedProducts = JSON.parse(relatedProducts);

    return parsedRelatedProducts.map(id => ({
      variantId: id,
      quantity: getNewQuantity(id, mainProductQuantityDifference),
    }));
  };

  const handleRemoveProduct = async isKeyDown => {
    // If we are removing the free item from cart,
    // we disable it from being automatically added
    if (lineItemIsFreeItem) {
      disableFreeItem();
    }

    if (hasFreeItemTag || hasFreeItemCustomAttribute) {
      const freeItemDiscountId = lineItem?.attributes?.find(
        attr => attr.key === '_freeItemDiscountId'
      )?.value;
      const raw = get(keys.AUTO_ADDED_FREE_ITEM_REMOVED);
      let removedIds = [];
      if (raw && raw !== 'true') {
        try {
          removedIds = JSON.parse(raw);
        } catch {
          removedIds = [];
        }
      }
      const idToTrack = freeItemDiscountId || '__all__';
      if (!removedIds.includes(idToTrack)) {
        removedIds.push(idToTrack);
      }
      set(keys.AUTO_ADDED_FREE_ITEM_REMOVED, JSON.stringify(removedIds));
    }

    const items = [
      {
        id: lineItem.id,
        variantId: lineItem.merchandise?.id,
        quantity: 0,
      },
      ...(lineItem.extraLines || []).map(l => ({ ...l, quantity: 0 })),
      ...getFreeItemsQuantity(lineItem.quantity),
    ];
    updateItemQuantity(items);

    if (!lineItemIsFreeItem && lineItem?.merchandise) {
      const productSelectListObj = trackedItemsSelectList?.find(
        p => p?.productId === parseShopifyGid(lineItem?.merchandise?.product.id)
      );
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductRemoveFromCartTracking({
        product: createTrackingPayload(lineItem, productSelectListObj, true),
      });
    }

    if (!isKeyDown) {
      await removeProduct(lineItem?.merchandise?.product_id);
    }
  };

  const handleUpdateQuantity = async value => {
    const items = [
      {
        id: lineItem.id,
        variantId: lineItem.merchandise?.id,
        customAttributes: lineItem.attributes,
        quantity: getNewQuantity(lineItem?.id, lineItem.quantity - value),
        sellingPlanId: lineItem.sellingPlanAllocation?.sellingPlan?.id,
      },
      ...getFreeItemsQuantity(lineItem.quantity - value),
    ];

    if (value > lineItem.quantity) {
      // cartLinesAdd merges into the existing line, so quantity is the delta.
      // The old +1 stepper hardcoded 1, which equals the delta when stepping by one;
      // the native select can jump multiple steps.
      items[0].quantity = value - lineItem.quantity;
      items[0].customAttributes = lineItem.attributes;
      await addItems(items);
    } else {
      const extraLines = lineItem.extraLines || [];
      if (extraLines.length) {
        items[0].quantity = value;
        items.push(...extraLines.map(l => ({ ...l, quantity: 0 })));
      }
      await updateItemQuantity(items);
    }

    const productSelectListObj = trackedItemsSelectList?.find(
      p => p?.productId === parseShopifyGid(lineItem?.merchandise?.product?.id)
    );
    if (value > lineItem.quantity) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductAddToCartTracking({
        product: createTrackingPayload(lineItem, productSelectListObj),
      });
    } else if (value < lineItem.quantity) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      useProductRemoveFromCartTracking({
        product: createTrackingPayload(lineItem, productSelectListObj, null),
      });
    }
  };

  const handleUpdateSellingPlan = async sellingPlanId => {
    await updateItemQuantity([
      {
        id: lineItem.id,
        variantId: lineItem.merchandise?.id,
        customAttributes: lineItem.attributes,
        quantity: lineItem.quantity,
        sellingPlanId: sellingPlanId || null,
      },
    ]);
  };

  const totalDiscount = lineItem.discountAllocations.reduce(
    (acc, curr) => acc + Number(curr.discountedAmount.amount),
    0
  );

  const getPrices = () => {
    const itemPrice = (Number(lineItem.merchandise?.priceV2?.amount) * lineItem.quantity).toFixed(
      2
    );
    const allocatedUnitAmount =
      lineItem.sellingPlanAllocation && lineItem.cost?.amountPerQuantity?.amount;
    const subscriptionPrice = allocatedUnitAmount
      ? (Number(allocatedUnitAmount) * lineItem.quantity).toFixed(2)
      : null;

    const discountedPrice = (() => {
      if (totalDiscount) {
        return Number(itemPrice - totalDiscount).toFixed(2);
      }
      if (subscriptionPrice && subscriptionPrice !== itemPrice) return subscriptionPrice;

      return null;
    })();

    return { finalPrice: itemPrice, discountedPrice };
  };

  return {
    handleRemoveProduct,
    handleUpdateQuantity,
    handleUpdateSellingPlan,
    getPrices,
    hasFreeItemTag,
    hasFreeItemCustomAttribute,
    lineItemIsFreeItem,
  };
}

export default useLineItemActions;
